import type { ComponentPropsWithRef } from "react";
import { forwardRef, useMemo } from "react";
import { omit } from "lodash";
import { DropdownElement as DropdownElementComponent } from "@/react/components/FormRenderer/FormContent/ElementTypes/DropdownElement";
import { classNames } from "@/react/helpers/twMergeWithCN";
import type { FieldElement } from "@/react/types/Element";
import { useBlocksContext } from "../../contexts/BlocksContext";

interface DropdownBlockMeta {
  elementableId: number;
  elementId: number;
  fieldIndex: number;
  position: number;
}

interface Option {
  id: string;
  value: string;
}

export interface FormDropdownBlockProps extends ComponentPropsWithRef<"div"> {
  blockInstanceMeta?: DropdownBlockMeta;
  label?: string;
  options?: Option[];
  required?: boolean;
}

export const FormDropdownBlock = forwardRef<
  HTMLDivElement,
  FormDropdownBlockProps
>(
  (
    {
      label = "Dropdown",
      required = false,
      options = [
        { id: "1", value: "Option 1" },
        { id: "2", value: "Option 2" },
        { id: "3", value: "Option 3" },
      ],
      className,
      blockInstanceMeta = {},
      ...rest
    },
    ref,
  ) => {
    const elementProps = omit(rest, ["blockPath"]);
    const { useFormRenderingContext } = useBlocksContext();
    const context = useFormRenderingContext();
    const fallbackIndex = useMemo(() => Date.now(), []);

    const optionValues = options.map(option => option.value);

    const element: FieldElement = {
      id: blockInstanceMeta.elementId ?? 0,
      elementable_id: blockInstanceMeta.elementableId ?? 0,
      position: blockInstanceMeta.position ?? 0,
      type: "field",
      attributes: {
        field_type: "dropdown",
        label,
        placeholder: null,
        required,
        hidden: false,
        options: optionValues,
      },
    };

    const errors =
      "backendErrors" in context ? (context.backendErrors ?? {}) : {};

    return (
      <div
        ref={ref}
        className={classNames("w-full", className)}
        {...elementProps}
      >
        <DropdownElementComponent
          element={element}
          fieldIndex={blockInstanceMeta.fieldIndex ?? fallbackIndex}
          errors={errors}
        />
      </div>
    );
  },
);

FormDropdownBlock.displayName = "FormDropdownBlock";
